import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { validateResetToken, resetPassword } from '../api/auth';
import { tokens } from '../theme/theme';

function normalizeToken(raw) {
  if (raw == null) return '';
  return decodeURIComponent(String(raw)).replace(/\/+$/, '');
}

function passwordRuleStatus(password) {
  const p = password || '';
  return {
    len: p.length >= 8,
    upper: /[A-Z]/.test(p),
    lower: /[a-z]/.test(p),
    num: /[0-9]/.test(p),
  };
}

const resetPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, 'At least 8 characters')
      .regex(/[A-Z]/, 'At least 1 uppercase letter')
      .regex(/[a-z]/, 'At least 1 lowercase letter')
      .regex(/[0-9]/, 'At least 1 number'),
    confirm_password: z.string().min(1, 'Confirm your password'),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

const ResetPassword = () => {
  const { token: tokenParam } = useParams();
  const navigate = useNavigate();
  const token = useMemo(() => normalizeToken(tokenParam), [tokenParam]);

  const [phase, setPhase] = useState('loading');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const runValidate = useCallback(async () => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    setPhase('loading');
    const res = await validateResetToken(token);
    if (!res.ok && res.network) {
      setPhase('network');
      return;
    }
    if (res.ok && res.valid) {
      setMaskedEmail(res.email || '');
      setPhase('valid');
    } else {
      setPhase('invalid');
    }
  }, [token]);

  useEffect(() => {
    runValidate();
  }, [runValidate]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const pwd = watch('new_password');
  const rules = passwordRuleStatus(pwd);

  const onSubmit = async (values) => {
    setSubmitError('');
    const result = await resetPassword(token, values.new_password);
    if (result.ok) {
      setResetSuccess(true);
      sessionStorage.setItem('loginPasswordResetToast', 'Password reset successfully');
      window.setTimeout(() => navigate('/login'), 2000);
    } else {
      setSubmitError(result.message);
    }
  };

  if (phase === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress aria-label="Checking reset link" />
      </Box>
    );
  }

  if (phase === 'network') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4, px: 2 }}>
        <Box sx={{ mx: 'auto', maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            We couldn&apos;t verify this link. Check your connection and try again.
          </Typography>
          <Button variant="contained" onClick={() => runValidate()} sx={{ textTransform: 'none' }}>
            Retry
          </Button>
          <Box sx={{ mt: 2 }}>
            <MuiLink component={Link} to="/login" variant="body2">
              Back to login
            </MuiLink>
          </Box>
        </Box>
      </Box>
    );
  }

  if (phase === 'invalid') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4, px: 2 }}>
        <Box sx={{ mx: 'auto', maxWidth: 400, width: '100%' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This link is invalid or has expired. Request a new reset link.
          </Alert>
          <Button component={Link} to="/forgot-password" variant="contained" fullWidth sx={{ textTransform: 'none', mb: 2 }}>
            Request new link
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <MuiLink component={Link} to="/login" variant="body2">
              Back to login
            </MuiLink>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
          px: 2,
        }}
      >
        <Box sx={{ mx: 'auto', maxWidth: 400, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ color: 'text.secondary' }}>
              Back to Home
            </Button>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Set a new password
            </Typography>
            {maskedEmail ? (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Resetting password for {maskedEmail}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Choose a strong password for your account.
              </Typography>
            )}
          </Box>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              bgcolor: 'background.paper',
              p: 4,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              boxShadow: tokens.shadow.sm,
            }}
          >
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}
            {resetSuccess ? (
              <Alert severity="success">Password reset successfully. Redirecting to sign in…</Alert>
            ) : null}

            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, pl: 0 }}>
              {[
                { ok: rules.len, label: 'At least 8 characters' },
                { ok: rules.upper, label: 'At least 1 uppercase letter' },
                { ok: rules.lower, label: 'At least 1 lowercase letter' },
                { ok: rules.num, label: 'At least 1 number' },
              ].map((r) => (
                <Typography
                  component="li"
                  key={r.label}
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.25,
                    color: r.ok ? 'success.main' : 'text.secondary',
                  }}
                >
                  <span aria-hidden>{r.ok ? '✓' : '×'}</span>
                  {r.label}
                </Typography>
              ))}
            </Box>

            <Controller
              name="new_password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="New password"
                  type={showPassword ? 'text' : 'password'}
                  size="small"
                  autoComplete="new-password"
                  error={!!errors.new_password}
                  helperText={errors.new_password?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'text.secondary', mr: 0.5 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="confirm_password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Confirm password"
                  type={showPassword ? 'text' : 'password'}
                  size="small"
                  autoComplete="new-password"
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'text.secondary', mr: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!isValid || resetSuccess}
              sx={{ py: 1.5, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
            >
              Reset password
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <MuiLink component={Link} to="/login" variant="body2">
                Back to login
              </MuiLink>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ResetPassword;
